import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown01Icon,
  ArrowUp01Icon,
  Cancel01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { toast } from "react-toastify";
import addressApi from "api/landlord/address";
import { syndicationFeedApi, SyndicationFeed } from "api/syndicationFeed";
import { isOverAffordableHousingComCap } from "./affordableHousingComCap";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Modal,
  Show
} from "shared/components";
import { Loader } from "shared/components/Loader";
import { Checkbox, CheckboxSize } from "shared/components/forms/Checkbox";
import { BaseInput } from "shared/components/forms/BaseInput";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { Unit } from "types/landlord/address";
import { apartmentsVariantForKind } from "./apartmentsVariant";
import { ADD_LISTINGS_LABEL } from "./constants";
import { getFeedErrorMessage } from "./feedErrorMessage";
import {
  feedListingsQueryKey,
  invalidateFeedQueries
} from "./useSyndicationFeed";

interface TreeProperty {
  id: number;
  name: string;
  units: Unit[];
}

interface TreeGroup {
  name: string;
  properties: TreeProperty[];
}

type IsEligible = (unit: Unit) => boolean;

interface AddListingsModalProps {
  feed: SyndicationFeed;
  feedLabel: string;
  opened: boolean;
  onClose: () => void;
}

const UNGROUPED_LABEL = "Other properties";
const FETCH_ALL_PER_PAGE = 1000;
const ADD_CONCURRENCY = 6;
// Skip layout/paint for off-screen rows so a large group/property expand
// doesn't block the main thread rendering hundreds of rows at once.
const UNIT_ROW_CV =
  "[content-visibility:auto] [contain-intrinsic-size:auto_1.75rem]";
const PROPERTY_ROW_CV =
  "[content-visibility:auto] [contain-intrinsic-size:auto_3rem]";

const chunk = <T,>(items: T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(items.length / size) }, (_, index) =>
    items.slice(index * size, index * size + size)
  );

const settleInBatches = (
  units: Unit[],
  worker: (unit: Unit) => Promise<unknown>,
  size: number
): Promise<PromiseSettledResult<unknown>[]> =>
  chunk(units, size).reduce<Promise<PromiseSettledResult<unknown>[]>>(
    async (accPromise, batch) => [
      ...(await accPromise),
      ...(await Promise.allSettled(batch.map(worker)))
    ],
    Promise.resolve([])
  );

const buildGroups = (units: Unit[]): TreeGroup[] => {
  const order: string[] = [];
  const byGroup = new Map<string, Map<number, TreeProperty>>();

  units.forEach((unit) => {
    const property = unit.property;
    if (!property) return;

    const groupName = property.property_group?.name ?? UNGROUPED_LABEL;
    let properties = byGroup.get(groupName);
    if (!properties) {
      properties = new Map();
      byGroup.set(groupName, properties);
      order.push(groupName);
    }

    let treeProperty = properties.get(property.id);
    if (!treeProperty) {
      treeProperty = { id: property.id, name: property.name, units: [] };
      properties.set(property.id, treeProperty);
    }
    treeProperty.units.push(unit);
  });

  return order.map((name) => ({
    name,
    properties: [...(byGroup.get(name)?.values() ?? [])]
  }));
};

const filterGroups = (groups: TreeGroup[], search: string): TreeGroup[] => {
  if (!search) return groups;

  return groups
    .map((group) => ({
      name: group.name,
      properties: group.properties
        .map((property): TreeProperty | null => {
          if (property.name.toLowerCase().includes(search)) return property;
          const units = property.units.filter((unit) =>
            (unit.title ?? "").toLowerCase().includes(search)
          );
          return units.length > 0 ? { ...property, units } : null;
        })
        .filter((property): property is TreeProperty => property !== null)
    }))
    .filter((group) => group.properties.length > 0);
};

interface SelectionState {
  checked: boolean;
  indeterminate: boolean;
}

const selectionFor = (
  eligibleUnits: Unit[],
  selected: Map<number, Unit>
): SelectionState => {
  if (eligibleUnits.length === 0)
    return { checked: false, indeterminate: false };
  const selectedCount = eligibleUnits.filter((unit) =>
    selected.has(unit.id)
  ).length;
  return {
    checked: selectedCount === eligibleUnits.length,
    indeterminate: selectedCount > 0 && selectedCount < eligibleUnits.length
  };
};

const collectEligibleUnits = (
  properties: TreeProperty[],
  isEligible: IsEligible
): Unit[] =>
  properties.flatMap((property) => property.units.filter(isEligible));

const UnitRow = memo(
  ({
    unit,
    eligible,
    selected,
    onToggle
  }: {
    unit: Unit;
    eligible: boolean;
    selected: boolean;
    onToggle: (unit: Unit) => void;
  }) => (
    <Checkbox
      size={CheckboxSize.SM}
      checked={selected}
      disabled={!eligible}
      onChange={() => onToggle(unit)}
      label={unit.title}
      wrapperClassName={UNIT_ROW_CV}
    />
  )
);
UnitRow.displayName = "UnitRow";

const PropertyRow = memo(
  ({
    property,
    expanded,
    selectedUnits,
    isEligible,
    onToggleExpand,
    onToggleUnit,
    onToggleAll
  }: {
    property: TreeProperty;
    expanded: boolean;
    selectedUnits: Map<number, Unit>;
    isEligible: IsEligible;
    onToggleExpand: (propertyId: number) => void;
    onToggleUnit: (unit: Unit) => void;
    onToggleAll: (units: Unit[], select: boolean) => void;
  }) => {
    const eligibleUnits = useMemo(
      () => property.units.filter(isEligible),
      [property.units, isEligible]
    );
    const { checked, indeterminate } = selectionFor(
      eligibleUnits,
      selectedUnits
    );

    return (
      <div className={PROPERTY_ROW_CV}>
        <div className="flex items-center justify-between border-y border-alpha-40 bg-background-900 py-3 pl-6 pr-4">
          <Checkbox
            label={property.name}
            checked={checked}
            indeterminate={indeterminate}
            disabled={eligibleUnits.length === 0}
            onChange={() => onToggleAll(eligibleUnits, !checked)}
            size={CheckboxSize.SM}
            wrapperClassName="min-w-0"
          />
          <IconButton
            HugeIcon={expanded ? ArrowUp01Icon : ArrowDown01Icon}
            size={IconButtonSize.SM}
            variant={IconButtonVariant.TEXT_BASE}
            onClick={() => onToggleExpand(property.id)}
          />
        </div>
        <Show if={expanded}>
          <div className="px-4 py-3">
            <div className="space-y-2 pl-4">
              {property.units.map((unit) => (
                <UnitRow
                  key={unit.id}
                  unit={unit}
                  eligible={isEligible(unit)}
                  selected={selectedUnits.has(unit.id)}
                  onToggle={onToggleUnit}
                />
              ))}
            </div>
          </div>
        </Show>
      </div>
    );
  }
);
PropertyRow.displayName = "PropertyRow";

const GroupSection = memo(
  ({
    group,
    expanded,
    expandedProperties,
    selectedUnits,
    isEligible,
    onToggleExpand,
    onTogglePropertyExpand,
    onToggleUnit,
    onToggleAll
  }: {
    group: TreeGroup;
    expanded: boolean;
    expandedProperties: Set<number>;
    selectedUnits: Map<number, Unit>;
    isEligible: IsEligible;
    onToggleExpand: (group: TreeGroup) => void;
    onTogglePropertyExpand: (propertyId: number) => void;
    onToggleUnit: (unit: Unit) => void;
    onToggleAll: (units: Unit[], select: boolean) => void;
  }) => {
    const eligibleUnits = useMemo(
      () => collectEligibleUnits(group.properties, isEligible),
      [group.properties, isEligible]
    );
    const { checked, indeterminate } = selectionFor(
      eligibleUnits,
      selectedUnits
    );

    return (
      <div>
        <div className="flex items-center justify-between border-y border-alpha-40 bg-background-800 px-4 py-3">
          <Checkbox
            label={group.name}
            checked={checked}
            indeterminate={indeterminate}
            disabled={eligibleUnits.length === 0}
            onChange={() => onToggleAll(eligibleUnits, !checked)}
            size={CheckboxSize.SM}
            wrapperClassName="min-w-0"
            labelClassName="!text-content-1000"
          />
          <IconButton
            HugeIcon={expanded ? ArrowUp01Icon : ArrowDown01Icon}
            size={IconButtonSize.SM}
            variant={IconButtonVariant.TEXT_BASE}
            onClick={() => onToggleExpand(group)}
          />
        </div>

        <Show if={expanded}>
          <div>
            {group.properties.map((property) => (
              <PropertyRow
                key={property.id}
                property={property}
                expanded={expandedProperties.has(property.id)}
                selectedUnits={selectedUnits}
                isEligible={isEligible}
                onToggleExpand={onTogglePropertyExpand}
                onToggleUnit={onToggleUnit}
                onToggleAll={onToggleAll}
              />
            ))}
          </div>
        </Show>
      </div>
    );
  }
);
GroupSection.displayName = "GroupSection";

export const AddListingsModal = ({
  feed,
  feedLabel,
  opened,
  onClose
}: AddListingsModalProps) => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch] = useDebouncedValue(searchTerm, 400);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  // Latest expandedGroups for toggleGroup to read without becoming an unstable
  // callback (adding it to deps would churn the memo'd GroupSection props).
  const expandedGroupsRef = useRef(expandedGroups);
  expandedGroupsRef.current = expandedGroups;
  const [expandedProperties, setExpandedProperties] = useState<Set<number>>(
    new Set()
  );
  const [selectedUnits, setSelectedUnits] = useState<Map<number, Unit>>(
    new Map()
  );

  const { data: unitsData, isLoading: isLoadingUnits } = useQuery({
    queryKey: ["ADD_LISTINGS_UNITS"],
    queryFn: (): Promise<{ items: Unit[] }> =>
      addressApi.getUnits({ per_page: FETCH_ALL_PER_PAGE }),
    enabled: opened
  });

  const { data: inFeedData } = useQuery({
    queryKey: [...feedListingsQueryKey(feed), "IN_FEED_IDS"],
    queryFn: () =>
      syndicationFeedApi.getFeedListings(feed, {
        per_page: FETCH_ALL_PER_PAGE
      }),
    enabled: opened
  });

  const inFeedUnitIds = useMemo(
    () =>
      new Set(
        (inFeedData?.items ?? [])
          .map((listing) => listing.unit?.id)
          .filter((id): id is number => typeof id === "number")
      ),
    [inFeedData]
  );

  const isEligible = useCallback<IsEligible>(
    (unit) =>
      Boolean(unit.listed) &&
      (unit.monthly_rent_amount?.cents ?? 0) > 0 &&
      !inFeedUnitIds.has(unit.id) &&
      !(
        feed === "affordable_housing_com" &&
        isOverAffordableHousingComCap(unit.monthly_rent_amount?.cents)
      ),
    [feed, inFeedUnitIds]
  );

  const search = debouncedSearch.trim().toLowerCase();
  const allGroups = useMemo(
    () => buildGroups(unitsData?.items ?? []),
    [unitsData]
  );
  const groups = useMemo(
    () => filterGroups(allGroups, search),
    [allGroups, search]
  );

  useEffect(() => {
    if (!search) {
      setExpandedGroups(new Set());
      setExpandedProperties(new Set());
      return;
    }
    setExpandedGroups(new Set(groups.map((group) => group.name)));
    setExpandedProperties(
      new Set(
        groups.flatMap((group) =>
          group.properties.map((property) => property.id)
        )
      )
    );
  }, [search, groups]);

  const handleClose = () => {
    setSearchTerm("");
    setExpandedGroups(new Set());
    setExpandedProperties(new Set());
    setSelectedUnits(new Map());
    onClose();
  };

  const toggleGroup = useCallback((group: TreeGroup) => {
    const willCollapse = expandedGroupsRef.current.has(group.name);
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      willCollapse ? next.delete(group.name) : next.add(group.name);
      return next;
    });
    // Prune the group's properties on collapse so a later re-expand starts clean
    // instead of showing properties left open from a prior search.
    if (willCollapse)
      setExpandedProperties((prev) => {
        const next = new Set(prev);
        group.properties.forEach((property) => next.delete(property.id));
        return next;
      });
  }, []);

  const toggleProperty = useCallback(
    (propertyId: number) =>
      setExpandedProperties((prev) => {
        const next = new Set(prev);
        next.has(propertyId) ? next.delete(propertyId) : next.add(propertyId);
        return next;
      }),
    []
  );

  const toggleUnit = useCallback(
    (unit: Unit) =>
      setSelectedUnits((prev) => {
        const next = new Map(prev);
        next.has(unit.id) ? next.delete(unit.id) : next.set(unit.id, unit);
        return next;
      }),
    []
  );

  const setUnitsSelected = useCallback(
    (units: Unit[], select: boolean) =>
      setSelectedUnits((prev) => {
        const next = new Map(prev);
        units.forEach((unit) =>
          select ? next.set(unit.id, unit) : next.delete(unit.id)
        );
        return next;
      }),
    []
  );

  const addMutation = useMutation({
    mutationKey: [...feedListingsQueryKey(feed), "ADD"],
    mutationFn: (units: Unit[]) =>
      settleInBatches(
        units,
        (unit) =>
          syndicationFeedApi.toggleFeedListing(feed, unit.id, {
            enabled: true,
            variant:
              feed === "apartments_com"
                ? apartmentsVariantForKind(unit.property?.kind)
                : undefined
          }),
        ADD_CONCURRENCY
      ),
    onSuccess: (results, units) => {
      invalidateFeedQueries(queryClient, feed);

      const succeededIds = units
        .filter((_, index) => results[index]?.status === "fulfilled")
        .map((unit) => unit.id);
      const failures = results.filter(
        (result): result is PromiseRejectedResult =>
          result.status === "rejected"
      );

      if (succeededIds.length > 0)
        toast.success(
          `${succeededIds.length} listing${succeededIds.length > 1 ? "s" : ""} added to the ${feedLabel} feed`
        );

      if (failures.length > 0) {
        setSelectedUnits((prev) => {
          const next = new Map(prev);
          succeededIds.forEach((id) => next.delete(id));
          return next;
        });
        toast.error(
          `${failures.length} couldn't be added: ${getFeedErrorMessage(failures[0].reason)}`
        );
        return;
      }

      handleClose();
    },
    onError: (error) => toast.error(getFeedErrorMessage(error))
  });

  const selectedCount = selectedUnits.size;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      panelClassName="m-4 flex max-h-[85vh] w-full max-w-[640px] flex-col"
    >
      <header className="flex items-center justify-between p-6 pb-4">
        <h2 className="text-xl font-bold text-content-1000">
          {ADD_LISTINGS_LABEL}
        </h2>
        <IconButton
          variant={IconButtonVariant.TEXT_BASE}
          size={IconButtonSize.LG}
          HugeIcon={Cancel01Icon}
          onClick={handleClose}
        />
      </header>

      <div className="px-4 pt-4">
        <BaseInput
          id="add-listings-search"
          type="search"
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search properties or units"
        />
      </div>

      <div className="mt-5 min-h-0 flex-1 overflow-y-auto">
        <Show if={isLoadingUnits}>
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        </Show>

        <Show if={!isLoadingUnits && groups.length === 0}>
          <p className="py-12 text-center text-sm text-content-400">
            No results found.
          </p>
        </Show>

        {groups.map((group) => (
          <GroupSection
            key={group.name}
            group={group}
            expanded={expandedGroups.has(group.name)}
            expandedProperties={expandedProperties}
            selectedUnits={selectedUnits}
            isEligible={isEligible}
            onToggleExpand={toggleGroup}
            onTogglePropertyExpand={toggleProperty}
            onToggleUnit={toggleUnit}
            onToggleAll={setUnitsSelected}
          />
        ))}
      </div>

      <footer className="flex items-center justify-end gap-2 border-t border-alpha-40 px-6 py-4">
        <Button
          variant={ButtonVariant.BASE}
          size={ButtonSize.BASE}
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.BASE}
          loading={addMutation.isPending}
          disabled={selectedCount === 0 || addMutation.isPending}
          onClick={() => addMutation.mutate([...selectedUnits.values()])}
        >
          {ADD_LISTINGS_LABEL}
        </Button>
      </footer>
    </Modal>
  );
};
